import { matchPath } from "react-router-dom"

/**
 * The brand half of the browser-tab title. Literal in every locale — only the
 * page half is translated (LOCKER-409 N1).
 */
export const TITLE_BRAND = "Mobilus"

/**
 * Every path-bearing route declared in `src/App.tsx`, mapped to the i18n key
 * that names its section, plus the `*` catch-all.
 *
 * Granularity is the section, never the entity on it: a dynamic route titles as
 * its section (`/sa/tenants/:tenantId` -> "Organizations"), because entity-name
 * titles were dropped from LOCKER-409 (OOS5).
 *
 * Keys are reused from `SaMenuItems` / `osMenuItems` / `usMenuItems` wherever the
 * menu already names the page correctly, and `pageTitle.*` keys cover the routes
 * no menu reaches. The title is a property of the ROUTE and not of the
 * `selectedMenuItemLabelKey` prop each page passes, so a page that mis-declares
 * that prop cannot produce a wrong title.
 *
 * `routeTitles.test.ts` walks `App.tsx` and fails if a route is added without an
 * entry here, or if an entry names a key that is missing from either locale.
 */
export const routeTitleKeys: Record<string, string> = {
  // Public / unauthenticated — no layout, so no menu key exists for these.
  "": "pageTitle.publicHome",
  "/": "pageTitle.publicHome",
  "/signup-info": "pageTitle.signupInfo",
  "/login": "login",
  "/signout": "signout",
  "/not-accessible": "pageTitle.notAccessible",

  // Organization / user space.
  "/us": "pageTitle.usHome",
  "/us/profile": "profile",
  "/us/organizations": "organizations",

  "/os": "pageTitle.osHome",
  "/os/iam": "iamModule",
  "/os/lockerWidget": "pageTitle.lockerWidget",
  "/os/lockers": "lockers",
  "/os/lockers/:lockerId": "lockers",
  "/os/lockers/:lockerId/controller": "pageTitle.lockerController",
  "/os/embedded/lockers": "lockers",
  "/os/reservations": "reservations",
  "/os/reservations/:reservationId": "reservations",
  "/os/reporting/:subPage": "reporting",
  "/os/configuration": "configurationModule",
  "/os/embedded/configuration": "configurationModule",
  "/os/clients": "clients",
  "/os/clients/:id": "clients",

  // Super-admin console.
  "/sa/home": "adminApp",
  "/sa/users": "users",
  "/sa/administrators": "administrators",
  "/sa/administrator/:id": "administrators",
  "/sa/org-members": "orgMembers",
  "/sa/tenants": "organizations",
  "/sa/tenants/:tenantId": "organizations",
  "/sa/lockers": "lockers",
  "/sa/lockers/:lockerId": "lockers",
  "/sa/reservation-syncs": "reservationSyncs",
  "/sa/reservation-syncs/:runId": "reservationSyncs",
  "/sa/sms-expeditors": "smsExpeditors",
  "/sa/email-expeditors": "emailExpeditors",
  "/sa/event-log": "meventLog",
  "/sa/admin-logs": "adminLogs",
  "/sa/seed-trackers": "seedTrackers",
  "/sa/sst": "sstApi",
  "/sa/sstv2": "sstv2Api",
  "/sa/sstv2/traces/:traceId": "sstv2Api",
  "/sa/sstv2/lockers/:lockerId": "sstv2Api",
  "/sa/sstv2-migration": "sstv2Migration",
  "/sa/somum": "somum",
  "/sa/reservations": "reservations",
  "/sa/notif-deliveries": "notifDeliveries",
  "/sa/reservation-audit": "reservationAudit",
  // Both title as the SECTION, per the granularity rule above: the report
  // detail page is one job of many, not a page of its own.
  "/sa/monthly-reports": "monthlyReports",
  "/sa/monthly-reports/:runId": "monthlyReports",
  "/sa/monthly-extracts/:runId": "monthlyReports",
  "/sa/sl1-import": "sl1Import",
  "/sa/sl1-import/:jobId": "sl1Import",
  "/sa/deleted-things": "deletedThings",
  "/sa/features": "features",
  "/sa/features/:id": "features",

  // Catch-all -> NotFound.
  "*": "pageTitle.notFound",
}

/**
 * `data-ui` surface id of every route in `routeTitleKeys` — same keys, one id each
 * (`dev/ecosystem/ui/sl2-console.md`). Stamped once on a wrapper around
 * `<SentryRoutes>` in `App.tsx`, so it also applies when iframed (unlike the title).
 * `routeTitles.test.ts` fails any route declared in `App.tsx` without an id here.
 */
export const routeUiIds: Record<string, `UI-SL2W-${string}`> = {
  "": "UI-SL2W-020",
  "/": "UI-SL2W-020",
  "/signup-info": "UI-SL2W-021",
  "/login": "UI-SL2W-022",
  "/signout": "UI-SL2W-023",
  "/not-accessible": "UI-SL2W-025",

  "/us": "UI-SL2W-030",
  "/us/profile": "UI-SL2W-031",
  "/us/organizations": "UI-SL2W-032",

  "/os": "UI-SL2W-040",
  "/os/iam": "UI-SL2W-110",
  "/os/lockerWidget": "UI-SL2W-070",
  "/os/lockers": "UI-SL2W-041",
  "/os/lockers/:lockerId": "UI-SL2W-045",
  "/os/lockers/:lockerId/controller": "UI-SL2W-067",
  "/os/embedded/lockers": "UI-SL2W-044",
  "/os/reservations": "UI-SL2W-080",
  "/os/reservations/:reservationId": "UI-SL2W-080",
  "/os/reporting/:subPage": "UI-SL2W-090",
  "/os/configuration": "UI-SL2W-100",
  "/os/embedded/configuration": "UI-SL2W-100",
  "/os/clients": "UI-SL2W-104",
  "/os/clients/:id": "UI-SL2W-104",

  "/sa/home": "UI-SL2W-120",
  "/sa/users": "UI-SL2W-121",
  "/sa/administrators": "UI-SL2W-125",
  "/sa/administrator/:id": "UI-SL2W-126",
  "/sa/org-members": "UI-SL2W-128",
  "/sa/tenants": "UI-SL2W-129",
  "/sa/tenants/:tenantId": "UI-SL2W-132",
  "/sa/lockers": "UI-SL2W-140",
  "/sa/lockers/:lockerId": "UI-SL2W-142",
  "/sa/reservation-syncs": "UI-SL2W-160",
  "/sa/reservation-syncs/:runId": "UI-SL2W-161",
  "/sa/sms-expeditors": "UI-SL2W-180",
  "/sa/email-expeditors": "UI-SL2W-185",
  "/sa/event-log": "UI-SL2W-200",
  "/sa/admin-logs": "UI-SL2W-201",
  "/sa/seed-trackers": "UI-SL2W-220",
  "/sa/sst": "UI-SL2W-222",
  "/sa/sstv2": "UI-SL2W-232",
  "/sa/sstv2/traces/:traceId": "UI-SL2W-246",
  "/sa/sstv2/lockers/:lockerId": "UI-SL2W-248",
  "/sa/sstv2-migration": "UI-SL2W-250",
  "/sa/somum": "UI-SL2W-231",
  "/sa/reservations": "UI-SL2W-214",
  "/sa/notif-deliveries": "UI-SL2W-189",
  "/sa/reservation-audit": "UI-SL2W-203",
  "/sa/monthly-reports": "UI-SL2W-276",
  "/sa/monthly-reports/:runId": "UI-SL2W-277",
  // Redirects to /sa/monthly-reports/:runId — the same surface.
  "/sa/monthly-extracts/:runId": "UI-SL2W-277",
  "/sa/sl1-import": "UI-SL2W-170",
  "/sa/sl1-import/:jobId": "UI-SL2W-174",
  "/sa/deleted-things": "UI-SL2W-207",
  "/sa/features": "UI-SL2W-270",
  "/sa/features/:id": "UI-SL2W-272",

  "*": "UI-SL2W-024",
}

/**
 * Static patterns first, then dynamic ones, so `/os/lockers` wins over
 * `/os/lockers/:lockerId`. The `*` entry is excluded — it is the fallback, not a
 * candidate.
 */
const orderedPatterns = (() => {
  const entries = Object.entries(routeTitleKeys).filter(
    ([pattern]) => pattern !== "*",
  )
  return [
    ...entries.filter(([pattern]) => !pattern.includes(":")),
    ...entries.filter(([pattern]) => pattern.includes(":")),
  ]
})()

/**
 * The i18n key naming the section a pathname belongs to.
 *
 * Every candidate goes through `matchPath`, including the static ones, so this
 * resolves a pathname the way React Router resolves it rather than the way an
 * object lookup would. That difference is the whole point: an exact
 * `routeTitleKeys[pathname]` is case- and slash-sensitive, so `/os/lockers/` and
 * `/OS/lockers` — both of which the router renders as the lockers list — would
 * miss every entry and title as "Page not found", the inverse of the confusion
 * this module exists to remove.
 *
 * Anything genuinely unmatched falls through to the `*` entry, which is the same
 * route React Router renders for it.
 */
export const resolveTitleKey = (pathname: string): string => {
  const match = orderedPatterns.find(([pattern]) =>
    matchPath({ path: pattern, caseSensitive: false, end: true }, pathname),
  )

  return match ? match[1] : routeTitleKeys["*"]
}

/** The `data-ui` surface id of the route a pathname renders — same matching as the title. */
export const resolveUiId = (pathname: string): string => {
  const match = orderedPatterns.find(([pattern]) =>
    matchPath({ path: pattern, caseSensitive: false, end: true }, pathname),
  )

  return routeUiIds[match ? match[0] : "*"]
}

/** `<page> - Mobilus`, per LOCKER-409 N1. */
export const buildDocumentTitle = (translatedPage: string): string =>
  `${translatedPage} - ${TITLE_BRAND}`
